import { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useHistory } from "react-router-dom";
import { useAIAssistant } from "../hooks/useAIAssistant";
import QuickReplyCategories from "./QuickReplyCategories";
import almaceniaAvatar from "../assets/alma-red.svg";
import TypingIndicator from "./TypingIndicator";
import VoiceInput from "./VoiceInput";

export const AIAssistantMobileTab = () => {
  const {
    conversations,
    conversationsLoading,
    currentConversationId,
    messages,
    sendLoading,
    sendError,
    isStreaming,
    activeTool,
    getConversationsList,
    getConversationById,
    deleteConversationById,
    startNewChat,
    sendMessage,
  } = useAIAssistant();

  const [input, setInput] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const history = useHistory();

  useEffect(() => {
    getConversationsList();
  }, [getConversationsList]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeTool]);

  const handleSend = useCallback((text) => {
    if (!text || text.length > 2000 || isStreaming) return;
    setInput("");
    sendMessage(text);
  }, [isStreaming, sendMessage]);

  const handleSelectConversation = (id) => {
    if (id === currentConversationId) return;
    getConversationById(id);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteConversationById(id);
  };

  const handleQuickReply = (userMessage) => {
    if (isStreaming) return;
    sendMessage(userMessage);
  };



  return (
    <MobileLayout>
      <MobileHeader>
        <HistoryToggle onClick={() => setShowHistory(true)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6h16" />
            <path d="M4 12h16" />
            <path d="M4 18h16" />
          </svg>
        </HistoryToggle>
        <BackButton onClick={startNewChat} disabled={isStreaming}>
          Nueva conversación
        </BackButton>
      </MobileHeader>

      {showHistory && <Overlay onClick={() => setShowHistory(false)} />}
      <HistoryDrawer $open={showHistory}>
        <HistoryDrawerHeader>
          <h4>Historial</h4>
          <CloseButton onClick={() => setShowHistory(false)}>×</CloseButton>
        </HistoryDrawerHeader>
        <NewChatInHistory onClick={() => { startNewChat(); setShowHistory(false); }} disabled={isStreaming}>
          Nueva conversación
        </NewChatInHistory>
        <HistoryList>
          {conversationsLoading && conversations.length === 0 && (
            <EmptyState>Cargando...</EmptyState>
          )}
          {conversations.length === 0 && !conversationsLoading && (
            <EmptyState>No hay conversaciones</EmptyState>
          )}
          {conversations.map((conv) => (
            <ConversationItem
              key={conv._id}
              $active={conv._id === currentConversationId}
              onClick={() => {
                handleSelectConversation(conv._id);
                setShowHistory(false);
              }}
            >
              <ConversationTitle>{conv.title || "Sin título"}</ConversationTitle>
              <ConversationDate>
                {conv.updatedAt
                  ? new Date(conv.updatedAt).toLocaleDateString()
                  : ""}
              </ConversationDate>
              <DeleteButton onClick={(e) => handleDelete(e, conv._id)}>
                ×
              </DeleteButton>
            </ConversationItem>
          ))}
        </HistoryList>
      </HistoryDrawer>

      {activeTool && <ToolBar>{activeTool}</ToolBar>}

      <MessagesContainer>
        {messages.length === 0 && (
          <>
            <WelcomeMessage>
              <WelcomeAvatar src={almaceniaAvatar} alt="ALMA" />
              <h3>¡Hola! soy ALMA</h3>
              <p>
                Tu socIA digital. Estoy aquí para ayudarte a vender más, fidelizar clientes y hacer crecer tu tienda. Selecciona un tema o pregúntame lo que necesites.
              </p>
            </WelcomeMessage>
            <QuickRepliesContainer>
              <QuickReplyCategories
                onSelectQuestion={handleQuickReply}
                disabled={isStreaming}
              />
            </QuickRepliesContainer>
          </>
        )}
        {messages.map((msg) => (
          <MessageRow key={msg.id} $role={msg.role}>
            <MessageBubble $role={msg.role}>
              {msg.role === "assistant" ? (
                msg.isStreaming && !msg.text ? (
                  <TypingIndicator />
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {msg.text || ""}
                  </ReactMarkdown>
                )
              ) : msg.role === "function" || msg.role === "functionCall" ? (
                <pre
                  style={{
                    margin: 0,
                    background: "#e8e8e8",
                    padding: "0.5rem",
                    borderRadius: "6px",
                    fontSize: "0.8em",
                  }}
                >
                  <code>{msg.text || "Function call"}</code>
                </pre>
              ) : (
                msg.text || ""
              )}
              {msg.isStreaming && msg.text && <CursorBlink>|</CursorBlink>}
            </MessageBubble>
          </MessageRow>
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {sendError && <ErrorBanner>{sendError}</ErrorBanner>}

      <InputArea>
        <VoiceInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          disabled={isStreaming}
          placeholder="Pregúntame sobre tu tienda..."
          rows={1}
        />
      </InputArea>
    </MobileLayout>
  );
};

/* ========== ESTILOS MOBILE ========== */

const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
  min-height: 300px;
  background: #fff;
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.8rem;
  border-bottom: 1px solid #eaeaea;
`;

const BackButton = styled.button`
  background: #fe0103;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-size: 0.9rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NewChatInHistory = styled.button`
  background: #fe0103;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  margin: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  width: calc(100% - 1rem);
  box-sizing: border-box;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToolBar = styled.div`
  padding: 0.5rem 0.8rem;
  font-size: 0.85rem;
  color: #666;
  background: #f5f5f5;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const WelcomeAvatar = styled.img`
  width: 72px;
  height: auto;
  margin-bottom: 0.5rem;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  color: #555;
  margin: auto;
  max-width: 300px;

  h3 {
    margin: 0 0 0.4rem;
    font-size: 1.2rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.4;
  }
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${({ $role }) => ($role === "user" ? "flex-end" : "flex-start")};
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 0.7rem 0.9rem;
  border-radius: 14px;
  font-size: 1rem;
  line-height: 1.45;
  background: ${({ $role }) => ($role === "user" ? "#000" : "#f5f5f5")};
  color: ${({ $role }) => ($role === "user" ? "#fff" : "#333")};
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;

  p,
  ul,
  ol,
  li,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    text-align: left;
    margin: 0 0 0.4rem;
  }

  p:last-child,
  ul:last-child,
  ol:last-child,
  li:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    padding-left: 1.2rem;
  }
`;

const CursorBlink = styled.span`
  animation: blink 1s step-start infinite;

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }
`;

const ErrorBanner = styled.div`
  padding: 0.6rem 0.8rem;
  background: #ffeaea;
  color: #c00;
  font-size: 0.9rem;
  border-top: 1px solid #ffcccc;
`;

const InputArea = styled.div`
  display: flex;
  gap: 0.4rem;
  padding: 0.6rem 0.8rem;
  border-top: 1px solid #eaeaea;
  background: #fff;
`;

const HistoryToggle = styled.button`
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.4rem;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
`;

const HistoryDrawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 80%;
  max-width: 320px;
  background: #fff;
  z-index: 101;
  display: flex;
  flex-direction: column;
  transform: translateX(${({ $open }) => ($open ? "0" : "-100%")});
  transition: transform 0.25s ease;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);
`;

const HistoryDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #eaeaea;

  h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #888;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const HistoryList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #888;
  font-size: 0.95rem;
  padding: 1rem;
`;

const ConversationItem = styled.div`
  padding: 0.7rem 0.6rem;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 0.3rem;
  position: relative;
  background: ${({ $active }) => ($active ? "#f5f5f5" : "transparent")};
  border: 1px solid ${({ $active }) => ($active ? "#000" : "transparent")};

  &:hover {
    background: #f5f5f5;
  }
`;

const ConversationTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 1.2rem;
`;

const ConversationDate = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-top: 0.2rem;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  background: transparent;
  border: none;
  color: #888;
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #c00;
  }
`;

const QuickRepliesContainer = styled.div`
  padding: 0 0.8rem 0.8rem;
`;
